import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useSiteConfig, pickI18n } from '../contexts/SiteConfigContext';
import { Instagram, Facebook, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const colors = config.identity?.colors || {};
  const contact = config.contact || {};
  const footer = config.footer || {};
  const year = new Date().getFullYear();

  const navItems = (config.nav || []).filter((n) => n.visible !== false);

  const labels = {
    de: { quick: 'Schnellzugriff', services: 'Services', contactUs: 'Kontaktieren Sie uns' },
    en: { quick: 'Quick Links', services: 'Services', contactUs: 'Contact Us' },
    ar: { quick: 'روابط سريعة', services: 'الخدمات', contactUs: 'تواصلي معنا' },
  };
  const L = labels[lang] || labels.de;

  return (
    <footer
      className="relative pt-20 pb-8 border-t border-pink-100"
      style={{ backgroundColor: colors.bg_light || '#fdf5f7' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-5">
              {config.identity?.logo && (
                <img
                  src={config.identity.logo}
                  alt=""
                  className="w-14 h-14 object-contain"
                  style={{ mixBlendMode: 'multiply' }}
                />
              )}
              <div
                className="font-display text-lg leading-tight"
                style={{ color: colors.text_dark || '#5a1d3b', fontWeight: 500 }}
              >
                {config.identity?.site_name || 'Dalaa Beauty'}
              </div>
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              {pickI18n(footer.tagline, lang)}
            </p>
            <div className="flex gap-2">
              {contact.instagram && (
                <a href={contact.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white border border-pink-200 flex items-center justify-center transition-colors" style={{ color: colors.primary || '#c4477a' }}>
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              {contact.facebook && (
                <a href={contact.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white border border-pink-200 flex items-center justify-center transition-colors" style={{ color: colors.primary || '#c4477a' }}>
                  <Facebook className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="w-10 h-10 rounded-full bg-white border border-pink-200 flex items-center justify-center transition-colors" style={{ color: colors.primary || '#c4477a' }}>
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4
              className="font-display text-base mb-5"
              style={{ color: colors.text_dark || '#5a1d3b', fontWeight: 500 }}
            >
              {L.quick}
            </h4>
            <ul className="space-y-3 text-sm">
              {navItems.map((l) => (
                <li key={l.key}>
                  <Link to={l.to} className="text-gray-600 hover:text-[#c4477a] transition-colors">
                    {pickI18n(l.label, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              className="font-display text-base mb-5"
              style={{ color: colors.text_dark || '#5a1d3b', fontWeight: 500 }}
            >
              {L.services}
            </h4>
            <ul className="space-y-3 text-sm">
              {(config.services || []).slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link to="/leistungen" className="text-gray-600 hover:text-[#c4477a] transition-colors">
                    {pickI18n(s.name, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              className="font-display text-base mb-5"
              style={{ color: colors.text_dark || '#5a1d3b', fontWeight: 500 }}
            >
              {L.contactUs}
            </h4>
            <ul className="space-y-3 text-sm">
              {contact.address && (
                <li className="flex gap-2.5 text-gray-600">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: colors.primary }} />
                  {contact.address}
                </li>
              )}
              {contact.phone && (
                <li className="flex gap-2.5 text-gray-600">
                  <Phone className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: colors.primary }} />
                  {contact.phone}
                </li>
              )}
              {contact.email && (
                <li className="flex gap-2.5 text-gray-600">
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: colors.primary }} />
                  {contact.email}
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-pink-100 text-center text-sm text-gray-500">
          © {year} {config.identity?.site_name || 'Dalaa Beauty'}. {pickI18n(footer.rights, lang)}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
